import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { ApprovalDataUser } from 'types/PostWorkflow';
import { mapUserToSelectOption } from 'app/modules/posts/utils/mapUserToSelectOption';
import StringUtils from 'utils/string';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Field,
  Select,
  TextLink,
} from '@kontentino/ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import TextEditor from 'app/modules/textEditor';
import { Editor } from '@tiptap/core';
import { TextEditorFeature } from 'app/modules/textEditor/constants/textEditorFeature';
import Skeleton from 'react-loading-skeleton';
import { UserRole } from 'types/User';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { useToast } from 'app/hooks/useToast';
import { useMutation } from 'react-query';
import PostTimeSaversApi from 'app/api/postTimeSavers';
import { REGEX_COMMENT_USER_MENTION } from '@kontentino/kontentino-constants/Regex';
import { useOpenBillingPage } from 'app/hooks/useBillingUrl';
import {
  TRIAL_INVITE_CAP,
  getApprovalRecipientCount,
  parseAdditionalEmails,
} from 'components/workflowOptions/sendForApproval/utils/recipientCount';

type Props = {
  type: ApprovalType;
  dataNamePrefix?: string;
  className?: string;
  initialSelectedUserIds: number[];
  onSelectedUsersChange: (selectedUsers: number[]) => void;
  subject: string;
  bodyMessage: string;
  onSubjectChange: (value: string) => void;
  onBodyMessageChange: (value: string) => void;
  formErrors: Record<string, string>;
  availableUsers: ApprovalDataUser[];
  additionalEmails?: string;
  onAdditionalEmailsChange?: (value: string) => void;
  isLoading?: boolean;
  autoScheduleNotice?: ReactNode;
};

type SelectOption = {
  value: number;
  name: string;
  label: JSX.Element;
  role: UserRole;
};

const MAX_SUGGESTED_USERS = 3;

const SaveAsTemplateButton: FC<{
  type: ApprovalType;
  message: string;
}> = ({ type, message }) => {
  const { t } = useTranslation();
  const [showSaved, setShowSaved] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveAsTemplateMutation = useMutation(
    PostTimeSaversApi.saveApprovalMessageBody,
    {
      onSuccess: () => {
        setShowSaved(true);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          setShowSaved(false);
        }, 2000);
      },
    },
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    if (saveAsTemplateMutation.isLoading || showSaved) return;

    saveAsTemplateMutation.mutate({
      type,
      value: message.replace(REGEX_COMMENT_USER_MENTION, '').trim(),
    });
  };

  const label = saveAsTemplateMutation.isLoading
    ? `${t('saving')}...`
    : showSaved
    ? t('saved')
    : t('saveAsTemplate');

  return (
    <Field.Caption
      cta={{
        label,
        onClick: handleClick,
      }}
    />
  );
};

const Form: FC<Props> = ({
  type,
  className,
  onSelectedUsersChange,
  initialSelectedUserIds,
  bodyMessage,
  onBodyMessageChange,
  formErrors,
  availableUsers,
  additionalEmails,
  onAdditionalEmailsChange,
  isLoading,
  autoScheduleNotice,
  dataNamePrefix = 'posts_send-for-approval',
}) => {
  const [isAdditionalFieldVisible, setAdditionalFieldVisible] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const { accountPlanType, isTrial } = useSubscriptionInfo();
  const openBillingPage = useOpenBillingPage();
  const textEditor = useRef<Editor>();
  const [userIds, setUserIds] = useState<number[]>([]);

  useEffect(() => {
    setUserIds(initialSelectedUserIds || []);
  }, [initialSelectedUserIds]);

  function exceedsTrialInviteCap(count: number) {
    if (!isTrial || count <= TRIAL_INVITE_CAP) {
      return false;
    }

    toast(
      t('approvalEmail.trialInviteHelper', { max: TRIAL_INVITE_CAP }),
      'warning',
    );

    return true;
  }

  const recipientCount = getApprovalRecipientCount({
    selectedUserIds: userIds,
    additionalEmails: additionalEmails || '',
  });

  function getLabel() {
    return type === 'client' ? t('clients') : t('recipients');
  }

  function handleSelectChange(
    options: SelectOption[],
    selectAction: {
      action: string;
      removedValue?: SelectOption;
    },
  ) {
    const selectedUsersIds = options.map((option) => option.value);

    const isAdditive =
      selectAction.action !== 'clear' &&
      selectAction.action !== 'remove-value' &&
      selectAction.action !== 'pop-value';

    if (
      isAdditive &&
      selectedUsersIds.length > userIds.length &&
      exceedsTrialInviteCap(
        getApprovalRecipientCount({
          selectedUserIds: selectedUsersIds,
          additionalEmails: additionalEmails || '',
        }),
      )
    ) {
      return;
    }

    setUserIds(selectedUsersIds);

    if (selectAction.action === 'clear') {
      textEditor.current?.commands.removeMentions(userIds);
    } else if (
      selectAction.action === 'remove-value' &&
      selectAction.removedValue
    ) {
      textEditor.current?.commands.removeMentions([
        selectAction.removedValue.value,
      ]);
    } else {
      options.forEach((user) => {
        if (!userIds.includes(user.value)) {
          textEditor.current?.commands.insertUniqueUserMention({
            id: user.value,
            name: user.name,
            surname: '',
            role: user.role,
          });
        }
      });
    }

    onSelectedUsersChange(selectedUsersIds);
  }

  const handleSelectUserSuggestion = (user: ApprovalDataUser) => {
    if (userIds.includes(user.id)) {
      return;
    }

    const updated = [...userIds, user.id];

    if (
      exceedsTrialInviteCap(
        getApprovalRecipientCount({
          selectedUserIds: updated,
          additionalEmails: additionalEmails || '',
        }),
      )
    ) {
      return;
    }

    setUserIds(updated);
    textEditor.current?.commands.insertUniqueUserMention({
      id: user.id,
      name: user.name,
      surname: '',
      role: user.role,
    });
    onSelectedUsersChange(updated);
  };

  const suggestedUsers = availableUsers
    .filter((user) => !userIds.includes(user.id))
    .slice(0, MAX_SUGGESTED_USERS);

  return (
    <div className={clsx('tw-flex tw-w-full tw-flex-col tw-gap-4', className)}>
      <Alert
        variant="info"
        data-name={`${dataNamePrefix}_links-notice`}
        data-cy="posts_send-for-approval_links-notice"
      >
        {t('approvalEmail.linksNotice')}
      </Alert>
      {autoScheduleNotice}
      <Field.Group>
        <Field.Label required>{getLabel()}</Field.Label>
        <div
          data-name={`${dataNamePrefix}_recipients-input`}
          data-cy="posts_send-for-approval_recipients-input"
        >
          <Select
            isMulti
            aria-invalid={!!formErrors.selectedUsers}
            closeMenuOnSelect={false}
            isDisabled={isLoading}
            value={availableUsers
              .filter((user) => userIds.includes(user.id))
              .map(mapUserToSelectOption)}
            options={availableUsers.map(mapUserToSelectOption)}
            filterOption={(option, input) =>
              StringUtils.isSubstring(input, option.data.name)
            }
            onChange={(options, actionMeta) => {
              handleSelectChange(options as SelectOption[], {
                action: actionMeta.action,
                removedValue: actionMeta.removedValue as SelectOption,
              });
            }}
            placeholder={`${t('select')} ${getLabel().toLowerCase()}...`}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (styles) => ({
                ...styles,
                zIndex: 1010,
              }),
            }}
          />
          {!isLoading && suggestedUsers.length > 0 && (
            <div
              className="tw-mt-2 tw-flex tw-items-baseline tw-space-x-1"
              data-name="suggestions-user-list"
              data-cy="suggestions-user-list"
            >
              <Field.Caption>{t('suggestions')}:</Field.Caption>
              <div className="tw-flex tw-flex-wrap tw-space-x-1">
                {suggestedUsers.map((user, index, arr) => (
                  <TextLink key={user.id}>
                    <Button
                      data-name="suggestion-user"
                      data-cy="suggestion-user"
                      variant="plain"
                      onClick={() => handleSelectUserSuggestion(user)}
                      className="tw-mr-1 tw-break-words tw-text-sm"
                    >
                      {user.name}
                      {index < arr.length - 1 ? ',' : ''}
                    </Button>
                  </TextLink>
                ))}
              </div>
            </div>
          )}
        </div>
        <Field.Error>{formErrors.selectedUsers}</Field.Error>
        {isTrial && (
          <Field.Caption>
            {t('approvalEmail.recipientsUsed', {
              count: recipientCount,
              max: TRIAL_INVITE_CAP,
            })}
          </Field.Caption>
        )}
        {!isAdditionalFieldVisible && (
          <Field.Caption
            cta={{
              label:
                type === 'client'
                  ? t('inviteClientsByEmail')
                  : t('inviteColleaguesByEmail'),
              onClick: () => setAdditionalFieldVisible(true),
              htmlAttributes: {
                'data-name': `${dataNamePrefix}_invite-clients-by-emails`,
              },
            }}
          />
        )}
      </Field.Group>
      {isAdditionalFieldVisible && (
        <Field.Group>
          <Field.Label>{`${
            type === 'client'
              ? t('additionalClientsByMail')
              : t('additionalColleaguesByEmail')
          }:`}</Field.Label>
          <Select
            isCreatable
            isMulti
            isDisabled={isLoading}
            options={[]}
            value={parseAdditionalEmails(additionalEmails ?? '').map(
              (email) => ({ label: email, value: email }),
            )}
            onChange={(options) => {
              const items = (options as Array<{ label: string; value: string }>)
                .map((opt) => opt.value.trim())
                .filter((email) => email.length > 0);

              const currentEmails = parseAdditionalEmails(
                additionalEmails || '',
              );

              if (
                items.length > currentEmails.length &&
                exceedsTrialInviteCap(
                  getApprovalRecipientCount({
                    selectedUserIds: userIds,
                    additionalEmails: items.join(', '),
                  }),
                )
              ) {
                return;
              }

              const addedEmails = items.filter(
                (email) => !currentEmails.includes(email),
              );
              const removedEmails = currentEmails.filter(
                (email) => !items.includes(email),
              );

              addedEmails.forEach((email) =>
                textEditor.current?.commands.insertEmailMention(
                  email,
                  type === 'client' ? 'client' : '',
                ),
              );
              if (removedEmails.length > 0) {
                textEditor.current?.commands.removeEmailMentions(removedEmails);
              }

              onAdditionalEmailsChange?.(items.join(', '));
            }}
            placeholder={`${t('forExample')}: mail@example.com`}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (styles) => ({
                ...styles,
                display: 'none',
              }),
              indicatorsContainer: (styles) => ({
                ...styles,
                display: 'none',
              }),
            }}
          />
          {isTrial && (
            <Field.Caption>
              {t('approvalEmail.trialInviteHelper', {
                max: TRIAL_INVITE_CAP,
              })}
            </Field.Caption>
          )}
        </Field.Group>
      )}
      <Field.Group>
        <Field.Label required>{t('body')}</Field.Label>
        {isLoading && <Skeleton height={120} />}

        {!isLoading && (
          <div className="tw-relative">
            <TextEditor
              classNames={{
                editor: 'tw-h-[120px]',
              }}
              accountPlanType={accountPlanType}
              dataPrefix="send-post-for-approval"
              placeholder={'Write a message...'}
              onDebouncedChange={(currentValue) =>
                onBodyMessageChange(currentValue)
              }
              initialValue={bodyMessage}
              disabled={isLoading || isTrial}
              onInit={(editor) => {
                textEditor.current = editor;
              }}
              features={[TextEditorFeature.UserMention]}
              mentionableUsers={[]}
            />
            {isTrial && (
              <div
                className="tw-mt-2 tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-bg-primary-10 tw-p-3"
                data-name={`${dataNamePrefix}_premium-gate`}
                data-cy="posts_send-for-approval_premium-gate"
              >
                <Badge variant="primary">
                  {t('approvalEmail.premiumBadge')}
                </Badge>
                <span className="tw-text-sm tw-text-grayscale-140">
                  {t('approvalEmail.trialGateTitle')}
                </span>
                <div className="tw-flex">
                  <Button
                    variant="special"
                    size="small"
                    data-name={`${dataNamePrefix}_premium-gate-upgrade-button`}
                    data-cy="posts_send-for-approval_premium-gate-upgrade-button"
                    onClick={() => openBillingPage()}
                  >
                    {t('approvalEmail.trialGateUpgradeCta')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <Field.Error>{formErrors.bodyMessage}</Field.Error>
        {!isLoading && (
          <div className="tw-flex tw-items-center tw-justify-between">
            <Field.Label
              className="tw-flex tw-cursor-not-allowed tw-items-center tw-gap-1"
              required
            >
              <Checkbox onChange={() => null} checked disabled size="small" />
              <span className="tw-text-xs tw-text-grayscale-100">
                {t('sendEmailNotification')}
              </span>
            </Field.Label>
            {!isTrial && (
              <SaveAsTemplateButton type={type} message={bodyMessage} />
            )}
          </div>
        )}
      </Field.Group>
    </div>
  );
};

export default Form;
